/** Состояние списка. */
import {DxAppError} from "../../dx-core/domain/error/dx-app-error";
import {DxGridRow} from "./dx-grid-row";


export class DxGridState {
  /** Режим работы: сингл селект\мульти селект. */
  private _allowMultiSelect: boolean = false;
  /** Текущее количество элементов в списке. */
  private _rowsCount: number = 0;
  /** Текущая модель фильтрации грида. */
  private _filterModel: any;
  /** Текущий элемент под фокусом (даже в случае мультиселекта он один). */
  private _activeRow: DxGridRow;
  /** Текущие выделенные элементы. */
  private _selectedRows: DxGridRow[] = [];
  /** Добавленные элементы. */
  private _addedRows: DxGridRow[] = [];
  /** Удаленные элементы. */
  private _removedRows: DxGridRow[] = [];
  /** Отредактированные элементы. */
  private _updatedRows: DxGridRow[] = [];
  /**
   * Предыдущее выделение.
   * Трэкать предыдущее выделение нужно для скидывания выдения при повторном клике по элементу.
   */
  private _prevSelectedRows: DxGridRow[] = [];


  /** Сбрасывает состояние списка. */
  public flush(): void {
    this._rowsCount = 0;
    if(this._activeRow) this._activeRow.isActive = false;
    this._activeRow = null;

    this._selectedRows.forEach(item => item.isSelected = false);
    this._selectedRows.length = 0;

    this._addedRows = [];
    this._removedRows = [];
    this._updatedRows = [];
    this._prevSelectedRows = [];
  }

  public get isDirty(): boolean {
    let isDirty: boolean = this._removedRows.length > 0;
    isDirty = isDirty || this._updatedRows.length > 0;
    isDirty = isDirty || this._addedRows.length > 0;
    return isDirty;
  }

  public get hasActiveRow(): boolean {
    return !!this._activeRow;
  }

  public get hasSelection(): boolean {
    return this._selectedRows && this._selectedRows.length > 0;
  }

  public get hasAddedRows(): boolean {
    return this._addedRows&& this._addedRows.length > 0;
  }

  public get hasUpdatedRows(): boolean {
    return this._updatedRows && this._updatedRows.length > 0;
  }


  public get hasRemovedRows(): boolean {
    return this._removedRows && this._removedRows.length > 0;
  }

  public get hasFilter(): boolean {
    if(!this._filterModel) return false;
    const keys = Object.keys(this._filterModel);
    return keys && keys.length > 0;
  }

  public flushFilter(): void {
    this._filterModel = null;
  }

  public addSelection(row: DxGridRow): void {
    if(this.hasActiveRow) this.clearActive();
    if(this._allowMultiSelect) {
      row.isSelected = true;
      this._selectedRows.push(row);
    } else {
      if(this.hasSelection) {
        // 1. Удаляем предыдущую отметку
        const result = this.removeSelection(this._selectedRows[0]);
        if(!result) {
          let msg = 'DxGridState в режиме single-select: ';
          msg += 'При обновлении выделения не найдено текущее выделение!';
          throw new DxAppError(msg);
        }
        // 2. Устанавливаем новую отметку
        row.isSelected = true;
        this._selectedRows.push(row);
      } else {
        row.isSelected = true;
        this._selectedRows.push(row);
      }
    }
  }


  /**
   * Обновляет строку в массиве добавленных строк при ее редактировании через инлайн-редакторы.
   * В этом случае наличие строки в массиве гарантируется самим механизмом добавления \ копирования.
   */
  public updateAddedRow(row: DxGridRow): void {
    const prevUpdate = this._addedRows.find(item => item.getRowId() === row.getRowId());
    if(prevUpdate) {
      const index = this._addedRows.indexOf(prevUpdate);
      this._addedRows[index] = row;
    }
  }


  public addUpdatedRow(row: DxGridRow): void {
    const prevUpdate = this._updatedRows.find(item => item.getRowId() === row.getRowId());
    if(prevUpdate) {
      const index = this._updatedRows.indexOf(prevUpdate);
      this._updatedRows[index] = row;
    } else {
      this._updatedRows.push(row);
    }
  }


  public removeSelection(row: DxGridRow): boolean {
    const index = this._selectedRows.indexOf(row);
    if(index !== -1) {
      this._selectedRows[index].isSelected = false;
      this._selectedRows.splice(index, 1);
      return true;
    }
    return false;
  }


  public makeActive(row: DxGridRow): void {
    if (this.hasActiveRow) this._activeRow.isActive = false;
    row.isActive = true;
    this._activeRow = row;
  }


  public isSelected(row: DxGridRow): boolean {
    return !!this._selectedRows.find(selectedRows => selectedRows.getRowId() === row.getRowId());
  }

  public isActive(row: DxGridRow): boolean {
    return this._activeRow && this._activeRow && this._activeRow.getRowId() === row.getRowId();
  }

  public canBeActive(row: DxGridRow): boolean {
    // Все просто. Элемент может быть активным если:
    // 1. Он уже не активный
    let allowActivation = !(this.hasActiveRow && this.isActive(row));
    // 2. Он не выбран
    allowActivation = allowActivation && !(this.hasSelection && this.isSelected(row));
    return allowActivation;
  }

  public clearActive(): void {
    this._activeRow.isActive = false;
    this._activeRow = null;
  }

  public clearSelected(): void {
    this._selectedRows.forEach(row => row.isSelected = false);
    this._selectedRows.length = 0;
  }

  set allowMultiSelect(value: boolean) {
    this._allowMultiSelect = value;
  }

  get rowsCount(): number {
    return this._rowsCount;
  }

  set rowsCount(value: number) {
    this._rowsCount = value;
  }

  get selectedRows(): DxGridRow[] {
    return this._selectedRows;
  }

  set selectedRows(value: DxGridRow[]) {
    this._selectedRows = value;
  }

  get removedRows(): DxGridRow[] {
    return this._removedRows;
  }

  set removedRows(value: DxGridRow[]) {
    this._removedRows = value;
  }

  get updatedRows(): DxGridRow[] {
    return this._updatedRows;
  }

  set updatedRows(value: DxGridRow[]) {
    this._updatedRows = value;
  }

  get prevSelectedRows(): DxGridRow[] {
    return this._prevSelectedRows;
  }

  set prevSelectedRows(value: DxGridRow[]) {
    this._prevSelectedRows = value;
  }

  get addedRows(): DxGridRow[] {
    return this._addedRows;
  }

  set addedRows(value: DxGridRow[]) {
    this._addedRows = value;
  }

  set filterModel(value: any) {
    this._filterModel = value;
  }
}
